import clsx from 'clsx';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import RoundedButton from 'components/RoundedButton';
import { useUserStore } from 'hooks/stores/useUserStore';
import mixpanel from 'mixpanel-browser';
import { observer } from 'mobx-react-lite';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { discountApiClient } from 'utils/apiClient';
import { copyToClipboard, getCta } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import styles from './PartnerDiscountModal.module.css';

const ModalState = {
  LOADING: 'LOADING',
  NO_ACCESS: 'NO_ACCESS',
  CODE_AVAILABLE: 'AVAILABLE',
};

export interface IPartnerDiscount {
  partnerCode: string;
  heroImage: string;
  iconComponent: any;
  discountComponent: any;
  conditionComponent: any;
  conditionTextCardOverride?: any;
}

interface IPartnerDiscountModal {
  partnerDiscount: IPartnerDiscount;
  handleDismiss: () => void;
}

const PartnerDiscountModal = observer(
  ({ partnerDiscount, handleDismiss }: IPartnerDiscountModal) => {
    const userStore = useUserStore();
    const {
      partnerCode,
      heroImage,
      iconComponent,
      discountComponent,
      conditionComponent,
    } = partnerDiscount;

    const [modalState, setModalState] = useState(ModalState.LOADING);
    const [codeInfo, setCodeInfo] = useState<{
      code?: string;
      link: string;
    } | null>(null);

    useEffect(() => {
      if (userStore.userInitialized) {
        if (userStore.currentUser) {
          (async () => {
            mixpanel.track('Discount Popup - Opened', {
              message: `User opened discount popup for ${partnerCode}`,
            });

            const result = await tryApi(
              discountApiClient.auth.getSubscriberPartnerDiscountCode({
                params: { partner_code: partnerCode },
              }),
            );

            if (result.status === HttpStatusCode.OK) {
              if (result.body.accessGranted && result.body?.codeInfo) {
                setCodeInfo(result.body?.codeInfo);
                setModalState(ModalState.CODE_AVAILABLE);
              } else {
                setModalState(ModalState.NO_ACCESS);
              }
            } else {
              setModalState(ModalState.NO_ACCESS);
              if (result.status === 'error') {
                throw result.error;
              }
            }
          })();
        } else {
          setModalState(ModalState.NO_ACCESS);
        }
      }
    }, [userStore.userInitialized, userStore.currentUser?.id]);

    return (
      <>
        <div className={styles.container}>
          <div className={styles.message}>
            <div className={clsx(styles.heroSection)}>
              <img src={heroImage} alt="Discount image" />
              <div
                className={clsx(
                  modalState === ModalState.NO_ACCESS
                    ? styles.noAccessBackground
                    : '',
                )}
              />
            </div>
            <img
              className={styles.closeButton}
              src={`${ASSET_PREFIX}/images/close_button_white.svg`}
              onClick={handleDismiss}
              alt="close button - cross"
            />
            {modalState === ModalState.NO_ACCESS && (
              <img
                className={styles.padlock}
                src={`${ASSET_PREFIX}/images/subscriber-perks/padlock-white.svg`}
                alt="padlock image"
              />
            )}

            <div className={clsx(styles.discountLogo)}>
              <div
                className={clsx(
                  styles.discountHeader,
                  modalState === ModalState.NO_ACCESS ? styles.dimmed : '',
                )}
              >
                <div className={styles.imageContainer}>{iconComponent}</div>
                <div className={styles.rightDiscountHeader}>
                  <h5 className={styles.discountComponent}>
                    {discountComponent}
                  </h5>
                  <p className={styles.conditionText}>{conditionComponent}</p>
                </div>
              </div>

              {modalState === ModalState.LOADING ? (
                <PlainLoadingSpinner style={{ marginBottom: '2em' }} />
              ) : modalState === ModalState.CODE_AVAILABLE &&
                codeInfo !== null ? (
                <>
                  {codeInfo.code && (
                    <>
                      <p className={styles.howToClaim}>
                        Enter this code at checkout to claim discount
                      </p>

                      <div className={styles.promoCode}>{codeInfo.code}</div>
                    </>
                  )}

                  <br />

                  {codeInfo.link && (
                    <RoundedButton
                      onClick={async () => {
                        if (codeInfo.code) {
                          copyToClipboard(codeInfo.code);
                        }
                        mixpanel.track('Discount Popup - Link Clicked', {
                          message: `User clicked link to open discount page for ${partnerCode}`,
                        });
                        await Router.push(codeInfo.link);
                      }}
                      className={styles.doneButton}
                      style={{
                        marginTop: 0,
                      }}
                    >
                      {codeInfo.code ? 'Copy Code & ' : ''}Launch Site
                    </RoundedButton>
                  )}
                </>
              ) : (
                <>
                  <p className={styles.subscribeToUnlock}>
                    Subscribe to unlock this discount
                  </p>

                  <RoundedButton
                    href={getCta()?.url}
                    className={styles.doneButton}
                  >
                    Subscribe Now
                  </RoundedButton>
                </>
              )}
            </div>
          </div>
        </div>
        <div
          onClick={handleDismiss}
          className={clsx(styles.dimmedBackground)}
        />
      </>
    );
  },
);

export default PartnerDiscountModal;
